import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTheme, useLoyalty } from "../../../../hooks";
import { logEvent } from "firebase/analytics";
import analytics from "../../../../context/analytics";

import { io } from "socket.io-client";
import useSpeedShoppingPoints from "../../speedShopping/hooks/useSpeedShoppingPoints";

const {
  REACT_APP_SERVER_URL: SERVER_URL,
  REACT_APP_WEBSOCKET_URL: WEBSOCKET_URL
} = process.env;

const CAPTURE_STATUS = {
  success: "success",
  outOfStock: "OUT_OF_STOCK",
  alreadyCaptured: "ALREADY_CAPTURED",
  error: "error",
}

const useCapturenShareStock = () => {
  const {
    captureAndShareData,
    captureImage,
    setCaptureImage,
    capture,
    setCapture,
    getPromoOfferFromStorage,
    fetchLoyalty,
    fetchCaptureShare,
    saveCaptureShare,
    captureStatus,
  } = useLoyalty();

  const {
    userPoints,
    getLoyaltyPoints,
  } = useSpeedShoppingPoints();

  const { rank } = userPoints || {};
  const [liveStock, setLiveStock] = useState(captureAndShareData?.promoData?.captureItems || []);
  const [filteredStock, setFilteredStock] = useState([]);
  const [socketConnection, setSocketConnection] = useState();
  const [currentItem, setCurrentItem] = useState(null);
  const [localCaptureShareData, setLocalCapturShareData] = useState(
    captureAndShareData || null
  );
  const [text, setText] = useState("Capture");
  const [shuffleId, setShuffleId] = useState(null);
  const [showCongratsModal, setShowCongratsModal] = useState(false);
  const [showOutOfStockModal, setShowOutOfStockModal] = useState(false);

  const { themeContext, theme } = useTheme();

  const history = useHistory();

  let themeColor = "background";
  let background = theme.colors[themeContext.activeTheme][themeColor];

  const initiateImageShuffle = () => {
    clearInterval(shuffleId);
    const items = filteredStock;
    if (items) {
      const intervalId = setInterval(() => {
        setCurrentItem(items[Math.floor(Math.random() * items?.length)]);
      }, 200);
      setShuffleId(intervalId);
    }
  }

  const handleCaptureImage = () => {
    setCapture(true);
    setCaptureImage(currentItem);
    saveCaptureShare(currentItem)
    setText("Processing...");
  };

  const filterCaptureStocks = useCallback((captureItems) => {
    return captureItems.filter(item => {
      const { quantity, used, rankStart, rankEnd } = item;
      return (quantity - used > 0) && (rank >= rankStart && rank <= rankEnd)
    })
  }, [rank]);

  const retryCapture = () => {
    setText("Capture");
    setCapture(false);
    setShowOutOfStockModal(false);
  }

  useEffect(() => {
    if (!!filteredStock?.length && !!rank) {
      initiateImageShuffle();
      return () => clearInterval(shuffleId);
    }
  }, [filteredStock, rank]);

  useEffect(() => {
    setCapture(false);
    fetchLoyalty();
    if (localCaptureShareData == null) {
      getPromoOfferFromStorage();
    }
  }, []);

  useEffect(() => {
    if (captureAndShareData !== null) {
      setLocalCapturShareData(captureAndShareData);
      const captureItems = captureAndShareData?.promoData?.captureItems;
      setFilteredStock(filterCaptureStocks(captureItems));
    }
  }, [captureAndShareData]);

  useEffect(() => {
    if (!!liveStock?.length) {
      setFilteredStock(filterCaptureStocks(liveStock));
    }
  }, [liveStock]);

  useEffect(() => {
    analytics().then((anl) =>
      logEvent(anl, "screen_view", {
        firebase_screen: "CaptureAndShareCapture",
        firebase_screen_class: "CaptureAndShareCapture",
      })
    );
    getLoyaltyPoints({ payload: {} });
  }, []);

  useEffect(() => {
    if (localCaptureShareData !== null) {
      fetchCaptureShare(localCaptureShareData?.code);
    }
  }, [localCaptureShareData]);

  useEffect(() => {
    if (captureStatus === CAPTURE_STATUS.success) {
      setShowCongratsModal(true);
    }
    if (captureStatus === CAPTURE_STATUS.outOfStock) {
      setShowOutOfStockModal(true);
    }
    if (captureStatus === CAPTURE_STATUS.alreadyCaptured) {
      history.push('/capturenshare/history')
    }
  }, [captureStatus]);

  useEffect(() => {
    // Connect to socket
    const socket = io(WEBSOCKET_URL, {
      transports: ["polling"],
    });
    socket.on("connect", () => {
      console.log("Connected");
    });

    socket.on("connect_error", (_) => {
      console.log("Could not connect to socket");
      setTimeout(() => {
        socket.connect();
      }, 1000);
    });
    setSocketConnection(socket);

    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    socketConnection?.on("captureShareStock", (msg) => {
      setLiveStock(msg);
    });
  }, [socketConnection]);

  return {
    history,
    captureAndShareData,
    captureImage,
    setCaptureImage,
    capture,
    setCapture,
    getPromoOfferFromStorage,
    fetchLoyalty,
    fetchCaptureShare,
    saveCaptureShare,
    captureStatus,
    currentItem,
    handleCaptureImage,
    background,
    text,
    showCongratsModal,
    showOutOfStockModal,
    setShowOutOfStockModal,
    retryCapture,
    loading: !filteredStock?.length
  }
}

export default useCapturenShareStock;